import { supabase } from '../../lib/supabase.js';
import type { SaasOrg } from './saasOrg';
import type { CompanySettingsLike } from '../../utils/companyBranding';
import type { PublicOrgSsoSettings } from './orgSsoConfig';

export interface PublicOrgUsageSummary {
  limits?: { bookings_per_month?: number; enquiries_per_month?: number };
  usage?: { bookings?: number; enquiries?: number };
}

export interface PublicOrgPayload {
  organization: SaasOrg & { legal_name?: string | null; custom_domain?: string | null };
  company_settings: CompanySettingsLike | null;
  usage_summary?: PublicOrgUsageSummary | null;
  sso_settings?: PublicOrgSsoSettings | null;
  /** Org pricing_config.publicAiChatEnabled (default true when omitted). */
  public_ai_chat_enabled?: boolean;
  /** Platform app_config saas_ai_addon_enabled (default true when omitted). */
  saas_ai_addon_enabled?: boolean;
  bubbles_ai_hosts?: string[];
  bubbles_ai_org_ids?: string[];
}

const ACTIVE_ORG_STATUSES = new Set(['active', 'trial', 'trialing', 'past_due', 'paused']);

const PUBLIC_ORG_SELECT =
  'id, slug, name, legal_name, status, industry, locale, tax_config, branding, trial_ends_at, onboarding_completed_at, settings, custom_domain';

const PUBLIC_COMPANY_SETTINGS_SELECT =
  'company_name, logo_url, tagline, phone, email, address_line1, address_line2, city, state, pincode, country, timezone, public_marketing_config, pricing_config';

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

type MembershipOrgRow = {
  organization_id: string;
  organizations: SaasOrg & { legal_name?: string | null; custom_domain?: string | null };
};

async function fetchPublicOrgSettings(orgId: string): Promise<CompanySettingsLike | null> {
  // Query by org id only. Do not go through fetchOrgCompanySettingsRow —
  // that helper pins the workspace and can re-enter fetchTbSiteOrganization
  // while this lookup is already in-flight (localhost User Management hang).
  const { data, error } = await supabase
    .from('company_settings')
    .select(PUBLIC_COMPANY_SETTINGS_SELECT)
    .eq('organization_id', orgId)
    .maybeSingle();
  if (error) return null;
  return (data as CompanySettingsLike | null) ?? null;
}

/** Resolve tenant from the signed-in user's membership (dev + owner preview). */
export async function fetchPublicOrgBySlugFromMembership(slug: string): Promise<PublicOrgPayload | null> {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return null;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return null;

  const { data, error } = await supabase
    .from('organization_members')
    .select(`organization_id, organizations!inner(${PUBLIC_ORG_SELECT})`)
    .eq('user_id', session.user.id)
    .eq('status', 'active');

  if (error || !data?.length) return null;

  // Supabase's generic client type-infers the `organizations!inner(...)` embed as an
  // array (its default for embedded resources), but this FK is many-to-one, so the
  // runtime shape is always a single object — matches the `.slug` access below.
  const match = (data as unknown as MembershipOrgRow[]).find(
    (row) => row.organizations?.slug?.trim().toLowerCase() === normalized,
  );
  const org = match?.organizations;
  if (!org || !ACTIVE_ORG_STATUSES.has(String(org.status ?? '').toLowerCase())) return null;

  const company_settings = await fetchPublicOrgSettings(org.id);
  const pricingConfig = (company_settings as { pricing_config?: unknown } | null)?.pricing_config;
  const publicAiChatEnabled =
    pricingConfig &&
    typeof pricingConfig === 'object' &&
    !Array.isArray(pricingConfig) &&
    typeof (pricingConfig as { publicAiChatEnabled?: unknown }).publicAiChatEnabled === 'boolean'
      ? Boolean((pricingConfig as { publicAiChatEnabled: boolean }).publicAiChatEnabled)
      : true;

  const { pricing_config: _pc, ...publicSettings } = (company_settings ?? {}) as Record<string, unknown> & {
    pricing_config?: unknown;
  };

  return {
    organization: org,
    company_settings: company_settings ? (publicSettings as typeof company_settings) : null,
    usage_summary: null,
    sso_settings: null,
    public_ai_chat_enabled: publicAiChatEnabled,
    saas_ai_addon_enabled: true,
  };
}

async function fetchPublicOrgFromApi(slug: string): Promise<PublicOrgPayload | null> {
  const res = await fetch(`/api/saas/public-org?slug=${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('public_org_lookup_failed');
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  return (await res.json()) as PublicOrgPayload;
}

/** Anon-safe lookup for hosts without /api/saas (toolsandbubbles.com). */
async function fetchPublicOrgFromRpc(filter: {
  slug?: string;
  host?: string;
}): Promise<PublicOrgPayload | null> {
  const { data, error } = await supabase.rpc('saas_resolve_public_org', {
    p_slug: filter.slug?.trim() || null,
    p_host: filter.host?.trim() || null,
  });
  if (error || !data) return null;
  return data as PublicOrgPayload;
}

export async function fetchPublicOrgBySlug(slug: string): Promise<PublicOrgPayload | null> {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return null;

  if (isLocalDevHost()) {
    const fromMembership = await fetchPublicOrgBySlugFromMembership(normalized);
    if (fromMembership) return fromMembership;
  }

  try {
    const fromApi = await fetchPublicOrgFromApi(normalized);
    if (fromApi) return fromApi;
  } catch {
    /* fall through */
  }

  const fromRpc = await fetchPublicOrgFromRpc({ slug: normalized });
  if (fromRpc) return fromRpc;

  return fetchPublicOrgBySlugFromMembership(normalized);
}

export async function fetchPublicOrgByHost(host: string): Promise<PublicOrgPayload | null> {
  const normalized = host.trim().toLowerCase();
  if (!normalized) return null;

  try {
    const res = await fetch(`/api/saas/public-org?host=${encodeURIComponent(normalized)}`);
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return (await res.json()) as PublicOrgPayload;
      }
    } else if (res.status !== 404) {
      throw new Error('public_org_lookup_failed');
    }
  } catch {
    /* T&B production has no /api/saas — use the anon RPC. */
  }

  return fetchPublicOrgFromRpc({ host: normalized });
}
